import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import styles from '../styles.module.css';
import { EditLocationForm, statusContainer } from './editLocationForm';
import { LocationApiResponse } from './types';
import {
  locationSchema,
  parseLocationSchema,
  showSuccessModal,
  handleApiError,
  combineTurbines,
  filterOutTurbine,
  createLocationUpdatePayload,
  filterLocalCompanies,
  filterLocalUsers,
  getPluralizedText,
  updateGeneralLocationData
} from './utils';
import { CompaniesAssignedTable } from '../AssignsTables/companiesTable';
import { DataPointTable } from '../AssignsTables/dataPointTable';
import { TurbinesAssignedTable } from '../AssignsTables/turbinesTable';
import { UsersAssignedTable } from '../AssignsTables/usersTable';
import { AdminDeleteSection } from '../UtilsContainers/delete';
import { AdminDetailHeader } from '../UtilsContainers/header';
import ConfirmDeleteIcon from '@/assets/icons/confirm-delete.svg?react';
import DeleteIcon from '@/assets/icons/delete.svg?react';
import ErrorIcon from '@/assets/icons/error-toast.svg?react';
import PowerSiteIcon from '@/assets/icons/power-site.svg?react';
import SuccessIcon from '@/assets/icons/success-icon.svg?react';
import WarningIcon from '@/assets/icons/warning.svg?react';
import { AdminCardButtons } from '@/components/AdminToolComponents/card';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import { Button } from '@/components/Form/Button';
import { PermissionsList } from '@/constants/permissionsList';
import adminStyles from '@/containers/AdminToolContainers/styles.module.css';
import useModal from '@/context/ModalContext/useModal';
import { useNavigationBar } from '@/context/NavigationBarContext';
import { useToast } from '@/context/ToastContext';
import {
  useGetLocationDetails,
  useGetLocationAccessLevels,
  useGetUpdateLocation,
  useDeleteUserCompany,
  useGetDataPoints,
  useUpdateLocationAccessLevels,
  LocationFormData,
  useDeleteLocation
} from '@/hooks/services/Locations';
import { useGetTurbineList } from '@/hooks/services/Turbines';
import { RootState } from '@/store';
import { TurbineApiData } from '@/types/turbines';
import { getLocationsPermissions } from '@/utils/adminToolPermissions';

function LocationDetail() {
  const { setCustomBreadcrumbArray } = useNavigationBar();
  const navigate = useNavigate();
  const auditableElements = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.permissions
  ) as PermissionsList;

  const userGroup = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.userGroup
  );

  const {
    canEditMainInfo,
    canAssignUnassignTurbines,
    canAssignUnassignUsers,
    canAssignUnassignCompanies,
    canEditDataPointVisibility,
    canDelete
  } = getLocationsPermissions(auditableElements, userGroup);

  const [formErrors, setFormErrors] = useState<{ [k: string]: string }>({});
  const [isEditing, setIsEditing] = useState(false);
  const [onSubmit, setOnSubmit] = useState(false);
  const [generalData, setGeneralData] = useState({
    id: '',
    name: '',
    numberOfTurbines: '',
    address: '',
    city: '',
    state: '',
    latitude: '',
    longitude: '',
    customer: '',
    nearestCity: '',
    startDate: '',
    status: 'Active',
    dataPoints: {} as Record<string, boolean>
  } as LocationFormData);
  const [searchTerm, _setSearchTerm] = useState('');
  const { updateModalContent, closeModal, showTypedModal } = useModal();
  const handleCloseModal = useCallback(() => {
    closeModal();
  }, [closeModal]);
  const { showToast } = useToast();

  const { locationId } = useParams<{ locationId: string }>();

  const handleError = useCallback(
    (error: string) => {
      showToast(error, {
        type: 'error',
        icon: ErrorIcon,
        filled: false,
        autoClose: 4000
      });
    },
    [showToast]
  );

  const handleWarning = useCallback(
    (warning: string) => {
      showToast(warning, {
        type: 'warning',
        icon: WarningIcon,
        filled: false,
        autoClose: 3000
      });
    },
    [showToast]
  );

  const updateLocationTurbinesMutation = useGetUpdateLocation();
  const updateLocationMutation = useGetUpdateLocation();
  const deleteUserCompanyMutation = useDeleteUserCompany();
  const updateLocationAccessLevelsMutation = useUpdateLocationAccessLevels();

  const { mutate: deleteLocation } = useDeleteLocation();
  const {
    data: dataTurbines,
    isFetching: isFetchingTurbines,
    isLoading: isLoadingTurbines,
    error: errorTurbines,
    isError: isErrorTurbines,
    refetch: refetchTurbines
  } = useGetTurbineList(locationId || '', {
    useQueryOptions: {
      staleTime: Infinity,
      retry: false,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true
    }
  });
  const turbinesData: TurbineApiData[] = dataTurbines?.data?.power || [];

  const {
    data: dataLocation,
    isFetching: isFetchingLocation,
    isLoading: isLoadingLocation,
    error: errorLocation,
    isError: isErrorLocation,
    refetch: refetchLocation
  } = useGetLocationDetails(locationId || '', {
    useQueryOptions: {
      staleTime: Infinity,
      retry: false,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true
    }
  });

  useEffect(() => {
    if (dataLocation && (dataLocation as LocationApiResponse)?.data) {
      setCustomBreadcrumbArray({
        label: (dataLocation as LocationApiResponse).data.name,
        path: `/admin-tool/locations/${(dataLocation as LocationApiResponse).data.id}`
      });
    }
  }, [dataLocation, setCustomBreadcrumbArray]);

  useEffect(() => {
    return () => {
      setCustomBreadcrumbArray({});
    };
  }, [setCustomBreadcrumbArray]);

  const {
    data: dataLocationAccessLevels,
    isFetching: isFetchingLocationAccessLevels,
    isLoading: isLoadingLocationAccessLevels,
    error: errorLocationAccessLevels,
    isError: isErrorLocationAccessLevels,
    refetch: refetchLocationAccessLevels
  } = useGetLocationAccessLevels(locationId || '', {
    useQueryOptions: {
      staleTime: Infinity,
      retry: false,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true
    }
  });

  const companiesData = dataLocationAccessLevels?.data?.companies || [];
  const usersData = dataLocationAccessLevels?.data?.users || [];

  const {
    data: dataPointsData,
    isLoading: isLoadingDataPoints,
    isFetching: isFetchingDataPoints,
    isError: isErrorDataPoints
  } = useGetDataPoints({
    useQueryOptions: {
      staleTime: Infinity,
      retry: false,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true
    }
  });

  const dataPointsLabels = dataPointsData?.labels;

  useEffect(() => {
    if (dataPointsLabels && Object.keys(generalData.dataPoints).length === 0) {
      const initialDataPoints: Record<string, boolean> = {};
      Object.keys(dataPointsLabels).forEach(key => {
        initialDataPoints[key] = false;
      });
      setGeneralData(prev => ({
        ...prev,
        dataPoints: initialDataPoints
      }));
    }
  }, [dataPointsLabels]);

  useEffect(() => {
    if ((dataLocation as LocationApiResponse)?.data && !isEditing) {
      const locationData = (dataLocation as LocationApiResponse).data;
      const updatedData = updateGeneralLocationData(
        locationData,
        turbinesData.length
      );

      if (dataPointsLabels) {
        const dataPointsWithAllKeys: Record<string, boolean> = {};
        Object.keys(dataPointsLabels).forEach(key => {
          dataPointsWithAllKeys[key] = updatedData.dataPoints[key] || false;
        });
        updatedData.dataPoints = dataPointsWithAllKeys;
      }
      setGeneralData(updatedData);
    }
  }, [
    (dataLocation as LocationApiResponse)?.data,
    turbinesData.length,
    isEditing,
    dataPointsLabels
  ]);

  const handleAssignTurbines = useCallback(
    async (
      turbines: TurbineApiData[],
      options?: { onSuccess?: () => void }
    ) => {
      if (!locationId) {
        console.error('No location ID available');
        return;
      }

      const allTurbines = combineTurbines(turbinesData, turbines);
      const locationData = (dataLocation as LocationApiResponse)?.data;

      try {
        await updateLocationTurbinesMutation.mutateAsync({
          data: createLocationUpdatePayload(
            locationData?.name || '',
            locationData?.category || '',
            allTurbines,
            Object.entries(generalData.dataPoints)
              .filter(([, value]) => value)
              .map(([key]) => key)
          ),
          id: locationId,
          isUnassign: false
        });

        if (options?.onSuccess) {
          options.onSuccess();
        } else {
          closeModal();
        }
        const { text: turbineText, verb: verbText } = getPluralizedText(
          turbines.length,
          'Turbine',
          'Turbines'
        );

        showSuccessModal(
          updateModalContent,
          {
            title: `${turbineText} ${verbText} assigned successfully`
          },
          refetchTurbines
        );
      } catch (error) {
        if (options?.onSuccess) {
          options.onSuccess();
        } else {
          closeModal();
        }
        console.error('Error assigning turbines:', error);

        const errorMessage = handleApiError(
          error,
          'There was an error assigning the turbines. Please try again.'
        );

        showToast(errorMessage, {
          type: 'error',
          autoClose: 5000,
          actionText: 'Try again',
          onActionClick: () => handleAssignTurbines(turbines, options)
        });
      }
    },
    [
      turbinesData,
      locationId,
      updateLocationTurbinesMutation,
      handleError,
      updateModalContent,
      closeModal,
      refetchTurbines,
      dataLocation
    ]
  );

  const handleUnassignTurbine = useCallback(
    async (turbineToUnassign: TurbineApiData) => {
      if (!locationId) {
        console.error('No location ID available');
        return;
      }

      const remainingTurbines = filterOutTurbine(
        turbinesData,
        turbineToUnassign
      );
      const locationData = (dataLocation as LocationApiResponse)?.data;

      try {
        await updateLocationTurbinesMutation.mutateAsync({
          data: createLocationUpdatePayload(
            locationData?.name || '',
            locationData?.category || '',
            remainingTurbines,
            Object.entries(generalData.dataPoints)
              .filter(([, value]) => value)
              .map(([key]) => key)
          ),
          id: locationId,
          isUnassign: true
        });

        closeModal();

        showSuccessModal(
          updateModalContent,
          {
            title: 'Turbine was unassigned successfully',
            icon: ConfirmDeleteIcon
          },
          refetchTurbines
        );
      } catch (error) {
        closeModal();
        console.error('Error unassigning turbine:', error);

        const errorMessage = handleApiError(
          error,
          'There was an error unassigning the turbine. Please try again.'
        );

        showToast(errorMessage, {
          type: 'error',
          autoClose: 5000,
          actionText: 'Try again',
          onActionClick: () => handleUnassignTurbine(turbineToUnassign)
        });
      }
    },
    [
      turbinesData,
      locationId,
      updateLocationTurbinesMutation,
      handleError,
      updateModalContent,
      closeModal,
      refetchTurbines,
      dataLocation
    ]
  );

  const handleAssignCompanies = useCallback(
    async (
      companies: Array<{ id: string; name: string }>,
      options?: { onSuccess?: () => void }
    ) => {
      if (!locationId) {
        console.error('No location ID available');
        return;
      }

      const existingCompanies = companiesData.map(
        (company: { id?: string; name?: string }) => ({
          id: company.id || '',
          name: company.name || ''
        })
      );

      const existingUsers = usersData
        .map((user: { id?: string; name?: string; username?: string }) => {
          const username = user.username || user.id || '';
          if (!username) {
            console.error('User missing username/id field:', user);
            return null;
          }
          return {
            name: user.name || username,
            username: username
          };
        })
        .filter(
          (
            user: { name: string; username: string } | null
          ): user is {
            name: string;
            username: string;
          } => user !== null
        );
      const allCompanies = [...existingCompanies];
      companies.forEach(newCompany => {
        if (!allCompanies.find(c => c.id === newCompany.id)) {
          allCompanies.push(newCompany);
        }
      });

      try {
        await updateLocationAccessLevelsMutation.mutateAsync({
          locationId,
          data: {
            companies: allCompanies,
            users: existingUsers
          }
        });

        if (options?.onSuccess) {
          options.onSuccess();
        } else {
          handleCloseModal();
        }
        const { text: companyText, verb: verbText } = getPluralizedText(
          companies.length,
          'Company',
          'Companies'
        );

        showSuccessModal(
          updateModalContent,
          {
            title: `${companyText} ${verbText} assigned successfully`
          },
          refetchLocationAccessLevels
        );
      } catch (error) {
        if (options?.onSuccess) {
          options.onSuccess();
        } else {
          handleCloseModal();
        }
        console.error('Error assigning companies:', error);

        const errorMessage = handleApiError(
          error,
          'There was an error assigning the companies. Please try again.'
        );

        showToast(errorMessage, {
          type: 'error',
          autoClose: 5000,
          actionText: 'Try again',
          onActionClick: () => handleAssignCompanies(companies, options)
        });
      }
    },
    [
      locationId,
      updateLocationAccessLevelsMutation,
      handleError,
      updateModalContent,
      closeModal,
      refetchLocationAccessLevels,
      handleCloseModal,
      companiesData,
      usersData
    ]
  );

  const handleUnassignCompany = useCallback(
    async (companyId: string) => {
      if (!locationId) {
        console.error('No location ID available');
        return;
      }

      try {
        await deleteUserCompanyMutation.mutateAsync({
          id: companyId,
          locationId
        });

        handleCloseModal();

        showSuccessModal(
          updateModalContent,
          {
            title: 'Company unassigned successfully',
            icon: ConfirmDeleteIcon
          },
          refetchLocationAccessLevels
        );
      } catch (error) {
        handleCloseModal();
        console.error('Error unassigning company:', error);

        const errorMessage = handleApiError(
          error,
          'There was an error unassigning the company. Please try again.'
        );

        showToast(errorMessage, {
          type: 'error',
          autoClose: 5000,
          actionText: 'Try again',
          onActionClick: () => handleUnassignCompany(companyId)
        });
      }
    },
    [
      locationId,
      deleteUserCompanyMutation,
      handleError,
      handleCloseModal,
      refetchLocationAccessLevels
    ]
  );

  const handleAssignUsers = useCallback(
    async (
      users: Array<{ name: string; username: string; id?: string }>,
      options?: { onSuccess?: () => void }
    ) => {
      if (!locationId) {
        console.error('No location ID available');
        return;
      }

      const existingCompanies = companiesData.map(
        (company: { id?: string; name?: string }) => ({
          id: company.id || '',
          name: company.name || ''
        })
      );

      const existingUsers = usersData
        .map((user: { id?: string; name?: string; username?: string }) => {
          const username = user.username || user.id || '';
          if (!username) {
            console.error('User missing username/id field:', user);
            return null;
          }
          return {
            name: user.name || username,
            username: username
          };
        })
        .filter(
          (
            user: { name: string; username: string } | null
          ): user is {
            name: string;
            username: string;
          } => user !== null
        );

      const newUsers = users.map(user => ({
        name: user.name,
        username: user.username || user.id || ''
      }));
      const allUsers = [...existingUsers];
      newUsers.forEach(newUser => {
        if (!allUsers.find(u => u.username === newUser.username)) {
          allUsers.push(newUser);
        }
      });

      try {
        await updateLocationAccessLevelsMutation.mutateAsync({
          locationId,
          data: {
            companies: existingCompanies,
            users: allUsers
          }
        });

        if (options?.onSuccess) {
          options.onSuccess();
        } else {
          handleCloseModal();
        }
        const { text: userText, verb: verbText } = getPluralizedText(
          users.length,
          'User',
          'Users'
        );

        showSuccessModal(
          updateModalContent,
          {
            title: `${userText} ${verbText} assigned successfully`
          },
          refetchLocationAccessLevels
        );
      } catch (error) {
        if (options?.onSuccess) {
          options.onSuccess();
        } else {
          handleCloseModal();
        }
        console.error('Error assigning users:', error);

        const errorMessage = handleApiError(
          error,
          'There was an error assigning the users. Please try again.'
        );

        showToast(errorMessage, {
          type: 'error',
          autoClose: 5000,
          actionText: 'Try again',
          onActionClick: () => handleAssignUsers(users, options)
        });
      }
    },
    [
      locationId,
      updateLocationAccessLevelsMutation,
      handleError,
      updateModalContent,
      closeModal,
      refetchLocationAccessLevels,
      handleCloseModal,
      companiesData,
      usersData
    ]
  );

  const handleUnassignUser = useCallback(
    async (username: string) => {
      if (!locationId) {
        console.error('No location ID available');
        return;
      }

      try {
        await deleteUserCompanyMutation.mutateAsync({
          id: username,
          locationId
        });

        handleCloseModal();

        showSuccessModal(
          updateModalContent,
          {
            title: 'User unassigned successfully',
            icon: ConfirmDeleteIcon
          },
          refetchLocationAccessLevels
        );
      } catch (error) {
        handleCloseModal();
        console.error('Error unassigning user:', error);

        const errorMessage = handleApiError(
          error,
          'There was an error unassigning the user. Please try again.'
        );

        showToast(errorMessage, {
          type: 'error',
          autoClose: 5000,
          actionText: 'Try again',
          onActionClick: () => handleUnassignUser(username)
        });
      }
    },
    [
      locationId,
      deleteUserCompanyMutation,
      handleError,
      handleCloseModal,
      refetchLocationAccessLevels
    ]
  );

  const setParsedLocationData = useCallback((formData: LocationFormData) => {
    const parsed = locationSchema.safeParse({
      ...formData
    });

    if (!parsed.success) {
      const errs = parseLocationSchema(parsed);
      setFormErrors(errs);
    } else {
      setFormErrors({});
    }

    return parsed;
  }, []);

  const handleSaveLocationData = useCallback(async () => {
    if (!locationId) {
      console.error('No location ID available');
      return;
    }

    setOnSubmit(true);
    const parsed = setParsedLocationData(generalData);

    if (parsed.success) {
      try {
        const locationData = (dataLocation as LocationApiResponse)?.data;
        const turbineIds = turbinesData.map(turbine => ({ id: turbine.id }));

        await updateLocationMutation.mutateAsync({
          data: createLocationUpdatePayload(
            generalData.name,
            locationData?.category || '',
            turbineIds,
            Object.entries(generalData.dataPoints)
              .filter(([, value]) => value)
              .map(([key]) => key),
            {
              address: generalData.address,
              city: generalData.city,
              state: generalData.state,
              latitude: generalData.latitude,
              longitude: generalData.longitude,
              customer: generalData.customer,
              startDate: generalData.startDate,
              status: generalData.status
            }
          ) as any,
          id: locationId,
          isUnassign: false
        });

        updateModalContent(
          <div className={styles.successModalContainer}>
            <div className={styles.successModalIcon}>
              <SuccessIcon />
            </div>
            <h2 className={styles.successModalTitle}>
              {`Location was successfully updated`}
            </h2>
          </div>,
          {
            modalTitle: '',
            modalButtons: [],
            autoClose: 3000
          }
        );
        setTimeout(() => {
          setIsEditing(false);
          setOnSubmit(false);
          closeModal();
          refetchLocation();
        }, 2000);
      } catch (error) {
        console.error('Error updating location:', error);
        setIsEditing(false);

        const errorMessage = handleApiError(
          error,
          'There was an error updating the location. Please try again.'
        );

        handleError(`Failed to update location: ${errorMessage}`);
      }
    }
  }, [
    locationId,
    generalData,
    dataLocation,
    turbinesData,
    updateLocationMutation,
    handleError,
    refetchLocation,
    setParsedLocationData
  ]);

  const handleDataChange = (field: string, value: string) => {
    if (field === 'id' || field === 'numberOfTurbines') {
      return;
    }

    const newFormData = {
      ...generalData,
      [field]: value
    };

    if (onSubmit) {
      setParsedLocationData(newFormData);
    }

    setGeneralData(newFormData);
  };

  const handleSelectFormData = (value: any) => {
    const newFormData = {
      ...generalData,
      customer: value[0].value
    };

    if (onSubmit) {
      setParsedLocationData(newFormData);
    }

    setGeneralData(newFormData);
  };

  const handleDataPointChange = (dataPoint: string, value: boolean) => {
    if (!value) {
      const selectedCount = Object.entries(generalData.dataPoints).filter(
        ([key, selected]) => key !== dataPoint && selected
      ).length;

      if (selectedCount === 0) {
        handleWarning(`At least one item must be selected`);
        return;
      }
    }

    setGeneralData(prev => ({
      ...prev,
      dataPoints: {
        ...prev.dataPoints,
        [dataPoint]: value
      }
    }));
  };

  const companiesForTable = useMemo(() => {
    return filterLocalCompanies(companiesData, searchTerm);
  }, [companiesData, searchTerm]);

  const usersForTable = useMemo(() => {
    return filterLocalUsers(usersData, searchTerm);
  }, [usersData, searchTerm]);

  const handleDelete = () => {
    deleteLocation(
      { id: locationId as string },
      {
        onSuccess: () => {
          showTypedModal({
            type: 'info',
            title: 'Location was successfully deleted',
            icon: ConfirmDeleteIcon
          });
          setTimeout(() => {
            closeModal();
            navigate('/admin-tool/locations');
          }, 2000);
        },
        onError: error => {
          console.error('Error deleting location:', error);
        }
      }
    );
  };

  const handleDeleteConfirmation = () => {
    updateModalContent(null, {
      modalTitle: 'Delete',
      modalIcon: DeleteIcon,
      modalButtons: [
        {
          label: 'CANCEL',
          onClick: () => {
            closeModal();
          },
          variant: 'cancelRed'
        },
        {
          label: 'DELETE PERMANENTLY',
          onClick: () => {
            handleDelete();
          },
          variant: 'okRed'
        }
      ],
      alignButtons: 'center',
      contentTitle: 'You are about to delete',
      contentSubtitle: `Location ${generalData.name}`,
      contentDescription: (
        <div className={styles.contentDescription}>
          <span>This action can not be undone.</span>
          <br />
          <span>Are you sure you want to proceed?</span>
        </div>
      )
    });
  };

  const hasLoadingError =
    errorTurbines ||
    isErrorTurbines ||
    errorLocation ||
    isErrorLocation ||
    errorLocationAccessLevels ||
    isErrorLocationAccessLevels ||
    isErrorDataPoints;

  useEffect(() => {
    if (hasLoadingError) {
      showToast('Something went wrong while loading location details', {
        type: 'error',
        autoClose: 5000,
        actionText: 'Try again',
        onActionClick: () => {
          refetchTurbines();
          refetchLocation();
          refetchLocationAccessLevels();
        }
      });
    }
  }, [
    hasLoadingError,
    showToast,
    refetchTurbines,
    refetchLocation,
    refetchLocationAccessLevels
  ]);

  if (
    isFetchingTurbines ||
    isLoadingTurbines ||
    isFetchingLocation ||
    isLoadingLocation ||
    isFetchingLocationAccessLevels ||
    isLoadingLocationAccessLevels ||
    isFetchingDataPoints ||
    isLoadingDataPoints
  ) {
    return <ContainerLoader />;
  }

  return (
    <div className={adminStyles.adminWrapper}>
      <div className={styles.adminContainer}>
        <AdminDetailHeader
          title={(dataLocation as LocationApiResponse)?.data?.name ?? ''}
          icon={PowerSiteIcon}
          isEditing={isEditing}
          onEditClick={() => setIsEditing(true)}
          editButtonLabel="EDIT"
          right={statusContainer({
            status: generalData.status as 'Active' | 'Inactive',
            handleChangeFormData: handleDataChange,
            showEditTurbine: isEditing
          })}
        />

        <EditLocationForm
          formData={{
            ...generalData,
            id: generalData.id || '',
            dataPointVisibility: []
          }}
          handleChangeFormData={handleDataChange}
          handleSelectFormData={handleSelectFormData}
          formErrors={formErrors}
          showEditTurbine={isEditing && canEditMainInfo}
        />
      </div>

      <DataPointTable
        formData={generalData}
        onDataPointChange={handleDataPointChange}
        dataPointsLabels={dataPointsLabels || undefined}
        editMode={isEditing && canEditDataPointVisibility}
      />

      {/* the following containers must hide at editing mode */}
      <div className={isEditing ? styles.hidden : ''}>
        <TurbinesAssignedTable
          turbinesForTable={turbinesData}
          canAssignUnassignTurbines={canAssignUnassignTurbines}
          onAssignTurbines={handleAssignTurbines}
          onUnassignTurbine={handleUnassignTurbine}
        />

        <div className={styles.divider}>Permissions</div>

        <CompaniesAssignedTable
          companiesForTable={companiesForTable}
          canAssignUnassignCompanies={canAssignUnassignCompanies}
          onAssignCompanies={handleAssignCompanies}
          onUnassignCompany={handleUnassignCompany}
        />

        <UsersAssignedTable
          usersForTable={usersForTable}
          canAssignUnassignUsers={canAssignUnassignUsers}
          onAssignUsers={handleAssignUsers}
          onUnassignUser={handleUnassignUser}
        />
      </div>

      {isEditing && canDelete && (
        <AdminDeleteSection
          title="Delete Location"
          onDeleteClick={handleDeleteConfirmation}
          text="This action can not be undone"
          buttonLabel="DELETE LOCATION PERMANENTLY"
        />
      )}

      {isEditing && (
        <AdminCardButtons>
          <Button
            color="blue"
            solid={false}
            onClick={() => {
              setIsEditing(false);
              setFormErrors({});
              setOnSubmit(false);
            }}
            disabled={updateLocationMutation.isPending}
            size="large"
            label="CANCEL"
          />
          <Button
            onClick={handleSaveLocationData}
            color="blue"
            label="SAVE"
            size="large"
            loading={updateLocationMutation.isPending}
          />
        </AdminCardButtons>
      )}
    </div>
  );
}

export default LocationDetail;
